import { ContextMenu } from '@base-ui/react/context-menu';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { Icon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { createElement } from 'react';
import { twMerge } from 'tailwind-merge';
import { popupPlacement, type PopupPlacement } from './popupPlacement';
import Tooltip from './Tooltip';

const ICON_SIZE = 18;

export type MenuItemIconProps = {
  color?: string;
  weight?: 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  size?: number | string;
  className?: string;
};

interface MenuItemProps {
  children: ReactNode;
  icon?: Icon;
  iconProps?: MenuItemIconProps;
  description?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Shown on hover when the item is disabled. */
  disabledReason?: string;
  divider?: boolean;
  selected?: boolean;
  className?: string;
  title?: string;
  /** Context-menu items must be Base UI context-menu items. The visuals stay the same. */
  contextMenu?: boolean;
}

interface MenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: PopupPlacement;
  offset?: [number, number];
  className?: string;
  positionerClassName?: string;
  menuItemsClassName?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  icon,
  iconProps,
  description,
  onClick,
  disabled = false,
  disabledReason,
  divider = false,
  selected = false,
  className = '',
  title,
  contextMenu = false
}) => {
  const hasDescription = Boolean(description);
  const baseClasses = twMerge(
    'flex w-full cursor-pointer gap-2 px-4 py-2 text-left text-sm text-content-default outline-none data-[highlighted]:bg-bluegray-100',
    hasDescription ? 'items-start' : 'items-center',
    disabled
      ? 'cursor-not-allowed opacity-50 data-[highlighted]:bg-transparent'
      : selected
        ? 'bg-bluegray-100'
        : 'hover:bg-bluegray-100',
    divider && 'border-b border-border-subtle',
    className
  );

  const Item = contextMenu ? ContextMenu.Item : BaseMenu.Item;
  const { className: iconClassName, ...restIconProps } = iconProps ?? {};

  const item = (
    <Item disabled={disabled} title={title} className={baseClasses} onClick={onClick}>
      {icon
        ? createElement(icon, {
            size: ICON_SIZE,
            weight: 'light',
            ...restIconProps,
            // No text colour here: the icon inherits the item colour, so a
            // destructive action (text-red-600) colours the glyph as well as the label.
            className: twMerge('shrink-0', hasDescription && 'mt-0.5', iconClassName)
          })
        : null}
      <span className="min-w-0 flex-grow">
        <span className={hasDescription ? 'block font-medium' : undefined}>{children}</span>
        {hasDescription ? <span className="mt-0.5 block text-xs text-content-subtle">{description}</span> : null}
      </span>
    </Item>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement="left">
        <span className="flex w-full">{item}</span>
      </Tooltip>
    );
  }

  return item;
};

function eventTargetIsInAnchor(anchorEl: HTMLElement | null, event: Event | undefined) {
  const target = event?.target;
  return anchorEl instanceof HTMLElement && target instanceof Node && anchorEl.contains(target);
}

export const Menu: React.FC<MenuProps> = ({
  anchorEl,
  open,
  onClose,
  children,
  placement = 'bottom-start',
  offset = [0, 4],
  className = '',
  positionerClassName = '',
  menuItemsClassName = ''
}) => {
  const { side, align } = popupPlacement(placement);
  const [skidding, distance] = offset;

  return (
    <BaseMenu.Root
      open={open && anchorEl != null}
      modal={false}
      onOpenChange={(next, eventDetails) => {
        if (next) return;
        if (eventTargetIsInAnchor(anchorEl, eventDetails.event)) return;
        onClose();
      }}
    >
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          anchor={anchorEl}
          side={side}
          align={align}
          alignOffset={skidding}
          sideOffset={distance}
          positionMethod="fixed"
          collisionPadding={8}
          className={twMerge('z-critical', positionerClassName)}
        >
          <BaseMenu.Popup
            className={twMerge(
              'z-critical max-w-[min(20rem,calc(100vw_-_2rem))] md:max-w-xs rounded-md bg-white py-1 shadow-md ring-1 ring-border-default outline-none',
              className
            )}
          >
            <div className={menuItemsClassName}>{children}</div>
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
};
